import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useAuthStore } from "@/stores/auth.store.js";
import { apiClient } from "@/lib/api-client.js";
import { StatusBadge } from "@/components/StatusBadge.js";
import { Card, CardHeader } from "@/components/ui/Card.js";
import { KpiTile } from "@/components/ui/KpiTile.js";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { Button } from "@/components/ui/Button.js";
import { EmptyState } from "@/components/ui/EmptyState.js";

interface ProjectItem {
  id: string;
  name: string;
  status: string;
  address: string;
  customer: { name: string };
  updatedAt: string;
}

interface ProjectListData {
  items: ProjectItem[];
  total: number;
}

interface StatusCount {
  status: string;
  count: number;
}

interface StatsData {
  projectCount: number;
  simulationCount: number;
  analysisCount: number;
  approvalsPending: number;
  latestProjectName: string | null;
  latestProjectStatus: string | null;
  projectsByStatus: StatusCount[];
}

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const SEASONAL = [0.70, 0.75, 0.85, 0.92, 0.97, 1.0, 1.0, 0.98, 0.93, 0.85, 0.73, 0.65];

const monthlyData = MONTHS.map((m, i) => ({
  monat: m,
  ertragFaktor: SEASONAL[i],
}));

const STATUS_COLORS: Record<string, string> = {
  draft: "#9ca3af",
  in_planning: "#2563eb",
  in_review: "#f59e0b",
  ready_for_offer: "#16a34a",
  archived: "#6b7280",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Entwurf",
  in_planning: "In Planung",
  in_review: "In Prüfung",
  ready_for_offer: "Angebotsbereit",
  archived: "Archiviert",
};

/** Platzhalter-Balken für KPI-Werte während des Ladens (statt „–"). */
function KpiSkeleton() {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 48,
        height: "0.8em",
        borderRadius: 4,
        background: "linear-gradient(90deg, var(--color-border) 25%, var(--color-surface-2, #f1f5f9) 50%, var(--color-border) 75%)",
        backgroundSize: "200% 100%",
        animation: "kpi-skeleton 1.2s ease-in-out infinite",
        verticalAlign: "middle",
      }}
    />
  );
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [projectsRes, statsRes] = await Promise.all([
          apiClient.get<ProjectListData>("/projects?pageSize=5"),
          apiClient.get<StatsData>("/stats"),
        ]);
        if (projectsRes.success) {
          setProjects(projectsRes.data.items);
          setTotal(projectsRes.data.total);
        }
        if (statsRes.success) {
          setStats(statsRes.data);
        } else {
          setStatsError("Statistik konnte nicht geladen werden.");
        }
      } catch {
        setStatsError("Statistik konnte nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const projectsByStatus: StatusCount[] = stats?.projectsByStatus ?? [];
  const fmt = (n: number | undefined) => (stats !== null && n !== undefined ? n : "–");

  return (
    <div>
      <style>{`@keyframes kpi-skeleton { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      <PageHeader
        title="Dashboard"
        subtitle="Überblick über Projekte, Simulationen und Freigaben"
        actions={
          <Link to="/projects/new">
            <Button icon="plus">Neues Projekt</Button>
          </Link>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-kpi" style={{ marginBottom: statsError ? "var(--space-2)" : "var(--space-6)" }}>
        <KpiTile label="Projekte gesamt" value={loading ? <KpiSkeleton /> : fmt(stats?.projectCount)} icon="projects" accent="primary" />
        <KpiTile label="Simulationen" value={loading ? <KpiSkeleton /> : fmt(stats?.simulationCount)} icon="simulation" accent="brand" />
        <KpiTile label="Wirtschaftlichkeitsanalysen" value={loading ? <KpiSkeleton /> : fmt(stats?.analysisCount)} icon="economics" accent="info" />
        <KpiTile label="Ausstehende Freigaben" value={loading ? <KpiSkeleton /> : fmt(stats?.approvalsPending)} icon="clock" accent="warning" />
      </div>
      {statsError && (
        <div style={{ marginBottom: "var(--space-6)", fontSize: 13, color: "var(--color-warning)" }}>
          {statsError}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-2" style={{ marginBottom: "var(--space-6)" }}>
        <Card>
          <CardHeader title="Projektstatus-Verteilung" />
          {projectsByStatus.length === 0 ? (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
              Noch keine Projektdaten
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={projectsByStatus} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80}>
                    {projectsByStatus.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] ?? "#6b7280"} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [v, STATUS_LABELS[name as string] ?? name]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {projectsByStatus.map((s) => (
                  <div key={s.status} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLORS[s.status] ?? "#ccc", flexShrink: 0 }} />
                    <span>{STATUS_LABELS[s.status] ?? s.status}: {s.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card>
          <CardHeader title="Solarertrag-Jahresverlauf (AT)" subtitle="Normierter Verlauf für Tirol/Kärnten" />
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyData} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="monat" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 1.1]} tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`} tick={{ fontSize: 10 }} width={38} />
              <Tooltip formatter={(v: unknown) => [`${((v as number) * 100).toFixed(0)}%`, "Ertragsfaktor"]} />
              <Bar dataKey="ertragFaktor" fill="var(--color-primary)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent projects */}
      <Card flush>
        <div style={{ padding: "var(--space-5) var(--space-5) 0" }}>
          <CardHeader
            title="Letzte Projekte"
            actions={<Link to="/projects" style={{ fontSize: 13, fontWeight: 600 }}>Alle anzeigen ({total}) →</Link>}
          />
        </div>

        {loading ? (
          <div style={{ color: "var(--color-text-muted)", textAlign: "center", padding: 24 }}>Lade…</div>
        ) : projects.length === 0 ? (
          <EmptyState
            icon="projects"
            title="Noch keine Projekte"
            description="Lege dein erstes Projekt an, um loszulegen."
            action={<Link to="/projects/new"><Button icon="plus">Erstes Projekt anlegen</Button></Link>}
          />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {["Projekt", "Kunde", "Adresse", "Status", "Zuletzt geändert"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/projects/${p.id}`}>{p.name}</Link></td>
                    <td className="muted">{p.customer.name}</td>
                    <td className="muted">{p.address}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="muted">{new Date(p.updatedAt).toLocaleDateString("de-AT")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ marginTop: "var(--space-4)", fontSize: 12, color: "var(--color-text-muted)" }}>
        Angemeldet als <strong>{user?.role}</strong> · Tenant {user?.tenantId.slice(0, 8)}…
      </div>
    </div>
  );
}
